import clsx from 'clsx';
import React from 'react';
import { useDevice } from '../../../providers/device-provider';
import { useCarousel } from './hooks/useCarousel';
import './index.css';
import { ControlPosition, ControlVariant, ICarouselProps } from './modal';
import NavbarController from './navbar-controller';
import NavbarIndicators from './navbar-indicators';
import Icon from '../../basic/icons';
import { useTranslation } from 'react-i18next';

const Carousel = (props: ICarouselProps): JSX.Element => {
  const { isMobile } = useDevice();
  const { t } = useTranslation();
  const [announcement, setAnnouncement] = React.useState('');
  const {
    carouselData,
    variant,
    displayNavbars,
    displayNavIndicators,
    displayNavControllers,
    positionNavControllers,
    autoSlideInSec,
    className,
    children,
    navControllersClassName,
    isAutoSlide,
    carouselInnerStyles,
    showFocus,
  } = props;
  const {
    carouselInnerRef,
    carouselSlideRef,
    carouselNavRef,
    navigateCarousel,
    pauseSlider,
    startCarouselInterval,
  } = useCarousel(autoSlideInSec as number, isAutoSlide as boolean);
  const [slideChanged, setSlideChanged] = React.useState(1);
  const [playSelected, setPlaySelected] = React.useState(true);
  const [pauseSelected, setPauseSelected] = React.useState(false);

  const navBarController = (controlVariant: ControlVariant, controlPosition: ControlPosition) => {
    return (
      <>
        <div role="status" aria-live="polite" className="sr-only" aria-atomic="true">
          {announcement}
        </div>
        <NavbarController
          variant={controlVariant}
          icon={controlVariant === 'left' ? 'leftArrow' : 'rightArrow'}
          className={navControllersClassName}
          positionNavControllers={controlPosition}
          showFocus={showFocus}
          target-testid={props['data-testid']}
          onClick={(e: any) => {
            setSlideChanged(slideChanged + 1);
            setAnnouncement(`Carousel slide content changed for ${slideChanged}th time`);
            navigateCarousel(controlVariant);
          }}
        />
      </>
    );
  };

  // By default not showing the nav controllers in Mobile view across the application
  // TODO: If at all any changes in future we read isMobile/new prop from the carousel props
  const isNavControlVisible =
    displayNavbars && displayNavControllers && !isMobile && carouselData?.length > 1;

  const isOnNavbar = isNavControlVisible && positionNavControllers === 'onNavbar';
  const isOnMedia = isNavControlVisible && positionNavControllers === 'onMedia';
  const isNavIndicatorVisible = displayNavIndicators && carouselData?.length > 1;

  return (
    <div
      id={props['data-testid']}
      className={clsx(
        'carousel slide relative',
        {
          'carousel-fade': variant === 'fade',
          'carousel-dark': variant === 'dark',
        },
        className,
      )}
      data-bs-ride="carousel"
      data-testid={props['data-testid']}
      data-interval="false"
      data-bs-interval="false"
      ref={carouselInnerRef}
      aria-label={props['aria-label']}
      role="group"
    >
      <div
        className={`carousel-inner relative w-full overflow-hidden justify-center items-center ${carouselInnerStyles}`}
        ref={carouselSlideRef}
      >
        {children}
        {/* this is used to extract the banner/carousel items as children*/}
      </div>

      {/* this is used to config carousel navbar indicators & controllers  button*/}
      <div className="flex justify-center xl:mb-md xl:mt-md md:mb-md md:mt-lg">
        {isNavIndicatorVisible && (
          <div className={`flex relative sm:top-12 lg:top-8 sm:mb-xl xl:mb-xs`}>
            <div
              className="carousel-indicators absolute right-0 bottom-0 left-0 flex space-x-xl justify-center p-0 mb-base h-md items-center"
              ref={carouselNavRef}
            >
              <div className="flex mt-2 text-center items-center">
                {isOnNavbar && navBarController('left', positionNavControllers)}
                <div
                  role="tablist"
                  className={`flex space-x-md nav-indicators pt-2 ml-6 ${!isNavIndicatorVisible && 'hidden'}`}
                >
                  {carouselData?.map((_obj: any, index: number) => {
                    return (
                      <NavbarIndicators
                        slide={index}
                        key={index}
                        role='tab'
                        active={index === 0 ? 'true' : ''}
                        target-testid={props['data-testid']}
                        onClick={() => {
                          navigateCarousel('', index + 1);
                        }}
                      />
                    );
                  })}
                </div>
                <button
                  role="button"
                  aria-pressed={playSelected}
                  data-testid="play-button"
                  aria-label={t('causel.play') || ''}
                  onClick={() => {
                    if (playSelected) return;
                    startCarouselInterval();
                    setPlaySelected(true);
                    setPauseSelected(false);
                  }}
                >
                  {' '}
                  <Icon name="play" className="pb-1 text-white"></Icon>
                </button>
                <button
                  role="button"
                  aria-pressed={pauseSelected}
                  data-testid="pause-button"
                  aria-label={t('causel.pause') || ''}
                  onClick={() => {
                    if (pauseSelected) return;
                    pauseSlider();
                    setPlaySelected(false);
                    setPauseSelected(true);
                  }}
                >
                  {' '}
                  <Icon name="pause" className="pb-1 text-white"></Icon>
                </button>
                {isOnNavbar && navBarController('right', positionNavControllers)}
              </div>
            </div>
          </div>
        )}
        {/* {isOnNavbar && navBarController('right', positionNavControllers)} */}
      </div>
      {isOnMedia && (
        <div className="flex flex-row absolute h-fit justify-between w-full left-0 right-0 top-0 bottom-0 m-auto">
          {navBarController('left', positionNavControllers)}
          {navBarController('right', positionNavControllers)}
        </div>
      )}
    </div>
  );
};

export default Carousel;
